import countries from '@/components/_dashboard/user/countries';
import type { MuiTelInputCountry } from 'mui-tel-input';

const DEFAULT_GIFT_CARD_PHONE_COUNTRY: MuiTelInputCountry = 'AU';
const supportedCountryCodes = new Set(countries.map((country) => country.code));

const toSupportedCountryCode = (value?: string | null): MuiTelInputCountry | null => {
    if (!value) {
        return null;
    }

    const normalizedValue = value.trim().toUpperCase();
    return supportedCountryCodes.has(normalizedValue) ? (normalizedValue as MuiTelInputCountry) : null;
};

const extractCountryCodeFromLocale = (locale: string): MuiTelInputCountry | null => {
    if (!locale.includes('-') && !locale.includes('_')) {
        return null;
    }

    const localeConstructor = typeof Intl !== 'undefined' ? Intl.Locale : undefined;

    if (localeConstructor) {
        try {
            const region = new localeConstructor(locale).maximize().region;
            const countryCode = toSupportedCountryCode(region);
            if (countryCode) {
                return countryCode;
            }
        } catch {
            // Ignore invalid locale values and fall back to string parsing.
        }
    }

    const localeMatch = locale.match(/[-_]([A-Za-z]{2})(?:$|[-_])/);
    return toSupportedCountryCode(localeMatch?.[1] ?? null);
};

export const resolveGiftCardPhoneCountryFromLocale = (locale?: string | null): MuiTelInputCountry => {
    if (!locale) {
        return DEFAULT_GIFT_CARD_PHONE_COUNTRY;
    }

    return extractCountryCodeFromLocale(locale) ?? DEFAULT_GIFT_CARD_PHONE_COUNTRY;
};

export const resolveSystemGiftCardPhoneCountry = (): MuiTelInputCountry => {
    if (typeof navigator === 'undefined') {
        return DEFAULT_GIFT_CARD_PHONE_COUNTRY;
    }

    const locales = [navigator.language, ...(navigator.languages || [])].filter(Boolean);

    for (const locale of locales) {
        const countryCode = extractCountryCodeFromLocale(locale);
        if (countryCode) {
            return countryCode;
        }
    }

    return DEFAULT_GIFT_CARD_PHONE_COUNTRY;
};
